import { useEffect, useState } from "react";
import { Moodies } from "@somethings-ui/core/assets";

const FORCE_DESKTOP_STORAGE_KEY = "mentor-dashboard-force-desktop";

export function isBlockedMobileDevice({
  userAgent,
  maxTouchPoints,
  coarseTouchPrimary,
  forceDesktop,
}: {
  userAgent: string;
  maxTouchPoints: number;
  coarseTouchPrimary: boolean;
  forceDesktop: boolean;
}): boolean {
  if (forceDesktop) return false;

  if (/iPad/i.test(userAgent)) return true;

  // iOS 13+ iPads report as Macintosh. Also require coarse/no-hover so a
  // Mac with a mouse still passes if a touchscreen sets maxTouchPoints > 1.
  if (
    /Macintosh/i.test(userAgent) &&
    maxTouchPoints > 1 &&
    coarseTouchPrimary
  ) {
    return true;
  }

  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent,
  );
}

function hasDesktopOverride(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get("desktop") === "1") {
    localStorage.setItem(FORCE_DESKTOP_STORAGE_KEY, "1");
    return true;
  }
  return localStorage.getItem(FORCE_DESKTOP_STORAGE_KEY) === "1";
}

/**
 * MobileBlocker
 *
 * Detects phone and iPad user agents on mount and renders a friendly
 * "desktop only" screen instead of the app.
 *
 * Blacklist approach: unknown/undetected devices always pass through.
 * Support override: `?desktop=1` persists in localStorage.
 *
 * Note: calls window.Intercom('show') directly rather than useIntercom() to
 * avoid re-booting Intercom with anonymous config and overwriting an
 * authenticated session already initialised by Sidebar.
 */
export function MobileBlocker({ children }: { children: React.ReactNode }) {
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    setIsBlocked(
      isBlockedMobileDevice({
        userAgent: navigator.userAgent,
        maxTouchPoints: navigator.maxTouchPoints,
        coarseTouchPrimary: window.matchMedia(
          "(hover: none) and (pointer: coarse)",
        ).matches,
        forceDesktop: hasDesktopOverride(),
      }),
    );
  }, []);

  if (!isBlocked) {
    return <>{children}</>;
  }

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-center gap-6 bg-primary-100 px-8 text-center">
      <img
        src={Moodies.happy}
        alt="Moodie"
        className="h-28 w-28"
      />

      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Desktop only</h1>
        <p className="max-w-xs text-base text-gray-600">
          The Somethings mentor dashboard is designed for desktop browsers.
          Please open it on a laptop or desktop computer to continue.
        </p>
      </div>

      <p className="text-sm text-gray-400">
        Need help?{" "}
        <button
          type="button"
          onClick={() => window.Intercom?.("show")}
          className="text-blue-brand underline underline-offset-2"
        >
          Contact support
        </button>
      </p>
    </div>
  );
}
